import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Search, Phone, Mail, MapPin, Building2 } from "lucide-react";
import { CATEGORIE, STATI, OPERATORI, categoriaLabel, statoLabel, statoBadgeClass, categoriaBadgeClass, initials } from "@/lib/format";
import { CLI } from "@/constants/testIds";
import ClientDialog from "@/components/app/ClientDialog";

export default function ClientiList() {
  const [params, setParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isScoped = Boolean(user?.operatore); // non-admin commerciale user

  const search = params.get("q") || "";
  const categoria = params.get("cat") || "tutti";
  const stato = params.get("st") || "tutti";
  const operatore = params.get("op") || "tutti";

  const setParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (val && val !== "tutti") next.set(key, val);
    else next.delete(key);
    setParams(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/clients", {
        params: {
          search: search || undefined,
          categoria: categoria !== "tutti" ? categoria : undefined,
          stato: stato !== "tutti" ? stato : undefined,
          operatore: operatore !== "tutti" ? operatore : undefined,
          limit: 2000,
        },
      });
      setClients(res.data);
    } finally {
      setLoading(false);
    }
  }, [search, categoria, stato, operatore]);

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("crm:data-changed", h);
    return () => window.removeEventListener("crm:data-changed", h);
  }, [load]);

  return (
    <div className="p-6 lg:p-8 max-w-[1600px]">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.16em] text-[#F56A1F] font-semibold mb-2">Anagrafica</div>
          <h1 className="text-4xl font-semibold tracking-tight text-stone-900">Clienti</h1>
          <p className="text-stone-500 mt-1">
            {clients.length} {clients.length === 1 ? "cliente" : "clienti"} in archivio
          </p>
        </div>
        <Button
          onClick={() => setOpenDialog(true)}
          className="bg-[#F56A1F] hover:bg-[#E05206] text-white gap-2 shadow-sm">
          <Plus className="w-4 h-4" /> Nuovo Cliente
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-stone-200 shadow-sm rounded-2xl p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              data-testid={CLI.searchInput}
              value={search}
              onChange={(e) => setParam("q", e.target.value)}
              placeholder="Cerca per ragione sociale, città, referente, email…"
              className="pl-9 h-10 bg-white"
            />
          </div>
          <Select value={categoria} onValueChange={(v) => setParam("cat", v)}>
            <SelectTrigger data-testid={CLI.filterCategoria} className="w-full md:w-56 bg-white h-10">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="tutti">Tutte le categorie</SelectItem>
              {CATEGORIE.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={stato} onValueChange={(v) => setParam("st", v)}>
            <SelectTrigger data-testid={CLI.filterStato} className="w-full md:w-44 bg-white h-10">
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="tutti">Tutti gli stati</SelectItem>
              {STATI.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!isScoped && (
          <Select value={operatore} onValueChange={(v) => setParam("op", v)}>
            <SelectTrigger data-testid="clienti-filter-operatore" className="w-full md:w-48 bg-white h-10">
              <SelectValue placeholder="Operatore" />
            </SelectTrigger>
            <SelectContent className="bg-white max-h-72">
              <SelectItem value="tutti">Tutti gli operatori</SelectItem>
              {OPERATORI.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card className="border-stone-200 shadow-sm rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50/70 border-b border-stone-200">
              <tr className="text-left text-[11px] uppercase tracking-widest text-stone-500 font-semibold">
                <th className="px-6 py-3">Cliente</th>
                <th className="px-6 py-3">Contatti</th>
                <th className="px-6 py-3">Referente</th>
                <th className="px-6 py-3">Categoria</th>
                <th className="px-6 py-3">Operatore</th>
                <th className="px-6 py-3">Stato</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-stone-400">Caricamento…</td></tr>
              ) : clients.length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-stone-400">Nessun cliente trovato.</td></tr>
              ) : clients.map((c) => (
                <tr
                  key={c.id}
                  data-testid={CLI.row(c.id)}
                  onClick={() => navigate(`/clienti/${c.id}`)}
                  className="border-b border-stone-100 hover:bg-stone-50/60 cursor-pointer transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-stone-100 text-stone-600 font-semibold flex items-center justify-center text-xs shrink-0">
                        {initials(c.ragione_sociale) || <Building2 className="w-4 h-4" />}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-stone-900 truncate max-w-[280px]">{c.ragione_sociale}</div>
                        {c.citta && (
                          <div className="text-xs text-stone-500 flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3 h-3" /> {c.citta}{c.provincia ? ` (${c.provincia})` : ""}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-stone-600">
                    {c.telefono && (
                      <div className="flex items-center gap-1.5 text-xs"><Phone className="w-3 h-3 text-stone-400" />{c.telefono}</div>
                    )}
                    {c.email && (
                      <div className="flex items-center gap-1.5 text-xs mt-0.5"><Mail className="w-3 h-3 text-stone-400" />
                        <span className="truncate max-w-[220px]">{c.email}</span>
                      </div>
                    )}
                    {!c.telefono && !c.email && <span className="text-stone-300">—</span>}
                  </td>
                  <td className="px-6 py-4 text-stone-700 text-sm">
                    {c.resp_acquisti_nome || <span className="text-stone-300">—</span>}
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="outline" className={`${categoriaBadgeClass(c.categoria)} text-xs`}>
                      {categoriaLabel(c.categoria)}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    {c.operatore_commerciale ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-stone-900 text-white">
                        {c.operatore_commerciale}
                      </span>
                    ) : (
                      <span className="text-stone-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="outline" className={`${statoBadgeClass(c.stato)} text-xs`}>
                      {statoLabel(c.stato)}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <ClientDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        onSaved={(c) => navigate(`/clienti/${c.id}`)}
      />
    </div>
  );
}
